import {
    confirmPermanentDeleteFolderSubText,
    confirmDeleteFolderSubText,
    confirmDeleteFolderText,
} from './deleteFolderInFolderForest.locstring.json';
import loc, { format } from 'owa-localize';
import selectFolder from './selectFolder';
import { ObservableMap } from 'mobx';
import { confirm, DialogResponse } from 'owa-confirm-dialog';
import { lazyRemoveFavoriteFolder } from 'owa-mail-favorites-store';
import { FolderForestNode, FavoriteFolderData } from 'owa-favorites-types';
import { favoritesStore } from 'owa-favorites';
import { getFolderIdForSelectedNode } from 'owa-mail-folder-forest-store';
import { getAllSubFolderIds } from 'owa-folders';
import deleteFolder from 'owa-mail-folder-store/lib/actions/deleteFolder';
import { lazyRemoveFolderIdFromMoveToMruList } from 'owa-mail-move-mru';
import { Folder } from 'owa-folders';
import folderStore, {
    FolderStore,
    PRIMARY_FOLDERS_TREE_TYPE,
    SHARED_FOLDERS_TREE_TYPE,
    isFolderOfTreeType,
} from 'owa-folders';
import folderNameToId from 'owa-session-store/lib/utils/folderNameToId';

import { action, select } from 'satcheljs/lib/legacy';
import { ActionSource } from 'owa-mail-store';
import { lazyMoveFolder } from 'owa-mail-folder-store';
import { isFeatureEnabled } from 'owa-feature-flags';

export interface DeleteFolderInFolderForestState {
    favoritesFolderNodesV1: ObservableMap<string, FolderForestNode>;
    favoritesFolderNodesV2: ObservableMap<string, FavoriteFolderData>;
    folders: FolderStore;
}

/**
 * Called on folders deleted
 * @param folderIds the folder ids that have been deleted
 * @param state the state which contains the favorite ids
 * @param deletedItemsFolderId deleted items folder id of corresponding mailbox
 */
export default action('deleteFolderInFolderForest')(
    select({
        favoritesFolderNodesV1: () => favoritesStore.favoritesFolderNodes,
        favoritesFolderNodesV2: () => favoritesStore.outlookFavorites,
        folders: () => folderStore,
    })(function deleteFolderInFolderForest(
        folderId: string,
        actionSource: ActionSource,
        deletedItemsFolderId: string,
        isFolderUnderDeletedItemFolder: boolean,
        state?: DeleteFolderInFolderForestState
    ) {
        const { favoritesFolderNodesV1, favoritesFolderNodesV2, folders } = state;
        const folderIdsToDelete = getAllSubFolderIds(folderId);

        // Only need to worry about removing the top-most folder from its parent's
        // list of children
        const folderToDelete: Folder = folders.folderTable.get(folderId);

        // TODO: VSO 8717: Show a different dialog when deleting a folder when folder with the same name exists under deleted items folder of
        // primary or archive mailbox
        const confirmationSubText = isFolderUnderDeletedItemFolder
            ? loc(confirmPermanentDeleteFolderSubText)
            : loc(confirmDeleteFolderSubText);

        // Popout the confirmation dialog
        confirm(
            loc(confirmDeleteFolderText),
            format(confirmationSubText, folderToDelete.DisplayName)
        ).then(async (response: DialogResponse) => {
            if (response === DialogResponse.ok) {
                folderIdsToDelete.forEach(id => {
                    // Try remove the folder id from the favorites if it's in favorites
                    tryRemoveFromFavorites(
                        id,
                        favoritesFolderNodesV1,
                        favoritesFolderNodesV2,
                        actionSource
                    );

                    // Reset selection to inbox if we are deleting selected folder or if it is a part of subtree being deleted
                    if (id == getFolderIdForSelectedNode()) {
                        selectFolder(folderNameToId('inbox'), 'primaryFolderTree' /* treeType */);
                    }
                });
                // For shared folders, we just move the folder to Deleted Items folder
                if (isFolderOfTreeType(folderId, SHARED_FOLDERS_TREE_TYPE)) {
                    return lazyMoveFolder.importAndExecute(
                        deletedItemsFolderId,
                        folderId,
                        PRIMARY_FOLDERS_TREE_TYPE,
                        true /* allowCrossMailboxMove */
                    );
                }

                // Remove the folder from the folder store and sends out deleteFolder service request
                return deleteFolder(
                    folders,
                    folderId,
                    isFolderUnderDeletedItemFolder /* softDelete */,
                    deletedItemsFolderId
                );
            }
        });

        // Update move to folder mru list
        lazyRemoveFolderIdFromMoveToMruList.importAndExecute(folderId);
    })
);

/**
 * Try remove the id from favorite folder ids if it's in the list
 * @param folderIds the folder ids that have been deleted
 * @param favoritesFolderNodes the favorite nodes V1
 * @param outlookFavoriteFolders the favorite nodes V2
 */
async function tryRemoveFromFavorites(
    idToRemove: string,
    favoritesFolderNodes: ObservableMap<string, FolderForestNode>,
    outlookFavoriteFolders: ObservableMap<string, FavoriteFolderData>,
    actionSource: ActionSource
) {
    if (isFeatureEnabled('tri-favorites-roaming')) {
        if (outlookFavoriteFolders.has(idToRemove)) {
            const removeFavoriteFolder = await lazyRemoveFavoriteFolder.import();
            removeFavoriteFolder(idToRemove, actionSource);
        }
    } else {
        if (favoritesFolderNodes.has(idToRemove)) {
            const removeFavoriteFolder = await lazyRemoveFavoriteFolder.import();
            removeFavoriteFolder(idToRemove, actionSource);
        }
    }
}
